import { Box } from "@mui/material";
// @mui
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";

// config
import { HEADER, NAVBAR } from "@/config";
import useCollapseDrawer from "@/hooks/useCollapseDrawer";
import useResponsive from "@/hooks/useResponsive";
// hooks
import useSettings from "@/hooks/useSettings";
import useUserPermissionsSync from "@/hooks/useUserPermissionsSync";

//
import DashboardHeader from "./header";
import NavbarHorizontal from "./navbar/NavbarHorizontal";
import NavbarVertical from "./navbar/NavbarVertical";

// ----------------------------------------------------------------------

const MainStyle = styled("main", {
    shouldForwardProp: (prop) => prop !== "collapseClick",
})<{ collapseClick?: boolean; isCollapse?: boolean }>(({ collapseClick, theme, isCollapse }) => ({
    marginTop: HEADER.MOBILE_HEIGHT + 24,
    height: `calc(100vh - ${HEADER.MOBILE_HEIGHT + 24}px)`,
    [theme.breakpoints.up("lg")]: {
        paddingLeft: 16,
        paddingRight: 16,
        marginTop: 24,
        width: `calc(100% - ${isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH}px)`,
        height: `calc(100vh - 24px)`,
        transition: theme.transitions.create("margin-left", {
            duration: theme.transitions.duration.shorter,
        }),
        ...(collapseClick && {
            marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
        }),
    },
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
    const { collapseClick, isCollapse } = useCollapseDrawer();
    const { themeLayout } = useSettings();
    const isDesktop = useResponsive("up", "lg");
    const [open, setOpen] = useState(false);
    const verticalLayout = themeLayout === "vertical";

    // Use permissions refresh hook
    const { refreshUserPermissions } = useUserPermissionsSync();

    // When sidebar is opened, refresh permissions
    useEffect(() => {
        if (open) {
            refreshUserPermissions();
        }
    }, [open, refreshUserPermissions]);

    if (verticalLayout) {
        return (
            <>
                {!isDesktop && <DashboardHeader onOpenSidebar={() => setOpen(true)} verticalLayout={verticalLayout} />}

                {isDesktop ? (
                    <NavbarHorizontal />
                ) : (
                    <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
                )}

                <Box
                    component="main"
                    sx={{
                        px: { lg: 2 },
                        pt: {
                            xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
                            lg: `80px`,
                        },
                        pb: {
                            xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
                            lg: `24px`,
                        },
                    }}
                >
                    <Outlet />
                </Box>
            </>
        );
    }

    return (
        <Box
            sx={{
                height: "100%",
                display: { lg: "flex" },
                minHeight: { lg: 1 },
            }}
        >
            {!isDesktop && <DashboardHeader isCollapse={isCollapse} onOpenSidebar={() => setOpen(true)} />}

            <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />

            <MainStyle collapseClick={collapseClick} isCollapse={isCollapse}>
                <Outlet />
            </MainStyle>
        </Box>
    );
}
